/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import VideoStopOverlayIcon from '@atlaskit/icon/core/video-stop-overlay';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoThinkingIndicator } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingIndicator';

const messages = defineMessages({
	title: {
		id: 'malleable-ui-components.remix-thinking-bar.title',
		defaultMessage: 'Rovo is building your visual aid',
		description: 'Text displayed while Rovo is generating a remix',
	},
	escHint: {
		id: 'malleable-ui-components.remix-thinking-bar.esc-hint',
		defaultMessage: 'Esc',
		description: 'Keyboard hint showing the Esc key can be used to cancel',
	},
	stopLabel: {
		id: 'malleable-ui-components.remix-thinking-bar.stop-label',
		defaultMessage: 'Stop remixing',
		description: 'Accessible label for the stop button',
	},
});

const styles = cssMap({
	fixedWrapper: {
		position: 'fixed',
		bottom: token('space.200'),
		left: token('space.050'),
		right: token('space.050'),
		width: 'fit-content',
		margin: '0 auto',
		zIndex: 600,
		paddingInline: token('space.200'),
		paddingBlock: token('space.100'),
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
		minWidth: '300px',
	},
	escBadge: {
		paddingInline: token('space.050'),
		paddingBlock: token('space.025'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
		color: token('color.text.subtlest'),
		borderRadius: token('radius.small'),
		font: token('font.body.small'),
	},
});

type RemixThinkingBarProps = {
	onStop: () => void;
	title?: string;
};

/**
 * A floating thinking bar that displays a Rovo loading animation, status text,
 * an Esc keyboard hint, and a stop button. Matches the visual style of the
 * inline chat ThinkingView / AI loading screen.
 */
export function RemixThinkingBar({ onStop, title }: RemixThinkingBarProps) {
	const { formatMessage } = useIntl();

	return (
		<Box as="div" xcss={styles.fixedWrapper} testId="remix-thinking-bar">
			<RovoThinkingIndicator title={title || formatMessage(messages.title)} />
			<Inline alignBlock="center" space="space.100">
				<Box as="span" xcss={styles.escBadge}>
					{formatMessage(messages.escHint)}
				</Box>
				<IconButton
					shape="circle"
					onClick={onStop}
					icon={(iconProps) => (
						<VideoStopOverlayIcon {...iconProps} color={token('color.icon.danger')} label="" />
					)}
					label={formatMessage(messages.stopLabel)}
				/>
			</Inline>
		</Box>
	);
}
